import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiService } from '@/services/api'
import { showNotification } from '@/utils/notifications'
import type { User } from '@/types/auth'

/**
 * Structure des credentials (identique au backend)
 * Le backend attend : { email, mot_de_passe }
 */
interface LoginCredentials {
  email: string
  mot_de_passe: string
}

interface RegisterCredentials {
  nom: string
  email: string
  mot_de_passe: string
}

/**
 * Réponse de login (identique au backend controllersAuth.js)
 */
interface LoginResponse {
  token: string
  user: User
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem('authToken'))
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Initialiser l'utilisateur depuis le localStorage (identique à Auth.getUser() de utils.js)
  const storedUser = localStorage.getItem('user')
  if (storedUser) {
    try {
      user.value = JSON.parse(storedUser)
    } catch (e) {
      console.error('Erreur lors du parsing de l\'utilisateur:', e)
    }
  }

  const isAuthenticated = computed(() => !!token.value && !!user.value)

  /**
   * Connexion (identique à la logique du frontend existant)
   * Payload : { email, mot_de_passe } comme attendu par le backend
   */
  async function login(credentials: LoginCredentials): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      // Le backend attend { email, mot_de_passe } dans controllersAuth.js ligne 97
      const response = await apiService.post<LoginResponse>('/auth/login', {
        email: credentials.email,
        mot_de_passe: credentials.mot_de_passe,
      })

      // Structure de réponse identique au backend (ligne 119-124 de controllersAuth.js)
      // Le backend retourne : { statut: 'succès', token: '...', user: {...} }
      const tokenValue = (response as any).token
      const userValue = (response as any).user || (response as any).data?.user
      if (response.statut === 'succès' && tokenValue && userValue) {
        token.value = tokenValue
        user.value = userValue as User

        // Stocker comme dans Auth.setAuth() de utils.js (ligne 31-34)
        if (tokenValue) localStorage.setItem('authToken', tokenValue)
        if (userValue) localStorage.setItem('user', JSON.stringify(userValue))

        return true
      }

      error.value = response.message || 'Erreur de connexion'
      return false
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur de connexion'
      error.value = message
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Inscription (identique à la logique du frontend existant)
   */
  async function register(credentials: RegisterCredentials): Promise<{ success: boolean; activationRequired?: boolean }> {
    isLoading.value = true
    error.value = null

    try {
      // Le backend attend { nom, email, mot_de_passe } dans controllersAuth.js ligne 39
      const response = await apiService.post<{ activation_required?: boolean }>('/auth/register', {
        nom: credentials.nom,
        email: credentials.email,
        mot_de_passe: credentials.mot_de_passe,
      })

      if (response.statut === 'succès') {
        return {
          success: true,
          activationRequired: response.activation_required || false,
        }
      }

      error.value = response.message || 'Erreur lors de l\'inscription'
      return { success: false }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur lors de l\'inscription'
      error.value = message
      return { success: false }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Vérifier la session (identique à Auth.getCurrentUser() de utils.js ligne 49-88)
   */
  async function checkSession(): Promise<boolean> {
    if (!token.value) {
      return false
    }

    // Vérifier si l'utilisateur est déjà en cache (ligne 56-59)
    if (user.value) {
      return true
    }

    try {
      // Le backend retourne { statut: 'succès', user: {...} } pour /auth/me
      const response = await apiService.get<User>('/auth/me')

      // Structure de réponse identique (ligne 77-80 de utils.js)
      if (response.statut === 'succès' && response.user) {
        user.value = response.user
        localStorage.setItem('user', JSON.stringify(response.user))
        return true
      }

      logout()
      return false
    } catch {
      logout()
      return false
    }
  }

  /**
   * Déconnexion (identique à Auth.clearAuth() de utils.js ligne 36-39)
   */
  function logout(showMessage = false): void {
    token.value = null
    user.value = null
    error.value = null

    localStorage.removeItem('authToken')
    localStorage.removeItem('user')
    
    // Afficher un message si demandé (session expirée)
    if (showMessage && typeof window !== 'undefined') {
      showNotification({
        message: 'Votre session a expiré. Veuillez vous reconnecter.',
        type: 'warning',
        duration: 5000,
      })
    }
  }

  /**
   * Vérifier si le token JWT est expiré
   */
  function isTokenExpired(): boolean {
    if (!token.value) return true
    
    try {
      // Décoder le token JWT (sans vérifier la signature, juste pour voir l'expiration)
      const payload = JSON.parse(atob(token.value.split('.')[1]))
      const expirationTime = payload.exp * 1000 // Convertir en millisecondes
      const currentTime = Date.now()
      
      // Vérifier si le token est expiré (avec une marge de 5 secondes)
      return currentTime >= (expirationTime - 5000)
    } catch (e) {
      // Si le token est invalide ou ne peut pas être décodé, considérer comme expiré
      return true
    }
  }

  /**
   * Vérifier et nettoyer la session si nécessaire
   */
  function validateSession(): boolean {
    if (!token.value || !user.value) {
      return false
    }
    
    // Vérifier l'expiration du token
    if (isTokenExpired()) {
      logout(true)
      return false
    }
    
    return true
  }

  return {
    token,
    user,
    isLoading,
    error,
    isAuthenticated,
    login,
    register,
    checkSession,
    logout,
    isTokenExpired,
    validateSession,
  }
})

